import { useEffect, useState } from 'react';
import useInboxStore from '@/stores/InboxStore';
import useTeamMemberStore from '@/stores/TeamMemberStore';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { setCalendarUrl } from '@/hooks/agent';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ButtonLoading } from '@/components/common/button/loading-button';

const SetPersonalLinkDialog = ({ isOpen, onOpenChange, agent }) => {
  const [calendarUrl, setCalendarUrlValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { updateAgentCalendarUrl } = useTeamMemberStore(
    useShallow((state) => ({
      updateAgentCalendarUrl: state.updateAgentCalendarUrl,
    }))
  );

  const { updateOwnerCalendarUrl } = useInboxStore(
    useShallow((state) => ({
      updateOwnerCalendarUrl: state.updateOwnerCalendarUrl,
    }))
  );

  useEffect(() => {
    if (agent && isOpen) {
      setCalendarUrlValue(agent.calendar_url || '');
    }
  }, [agent, isOpen]);

  const handleClose = () => {
    onOpenChange(false);
    setCalendarUrlValue('');
  };

  const handleSave = async () => {
    if (!agent) return;

    try {
      setIsLoading(true);
      const response = await setCalendarUrl(agent._id, calendarUrl);

      // Update both stores
      updateAgentCalendarUrl(agent._id, calendarUrl);
      updateOwnerCalendarUrl(agent._id, calendarUrl);

      toast.success('Personal link updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to update personal link'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      {agent && (
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Set Personal Link</DialogTitle>
            <DialogDescription>
              Set personal calendar link for {agent.first_name}{' '}
              {agent.last_name}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-3'>
            <div className='space-y-2'>
              <Label htmlFor='calendar-url'>Personal Link</Label>
              <Input
                id='calendar-url'
                type='url'
                placeholder='https://example.com'
                value={calendarUrl}
                onChange={(e) => setCalendarUrlValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading) {
                    handleSave();
                  }
                }}
              />
            </div>
          </div>

          <DialogFooter className='mt-5'>
            <div className='flex w-full flex-row items-center justify-end'>
              <div className='flex space-x-2'>
                <Button variant='secondary' onClick={handleClose}>
                  Cancel
                </Button>
                {isLoading ? (
                  <ButtonLoading />
                ) : (
                  <Button onClick={handleSave}>Save</Button>
                )}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default SetPersonalLinkDialog;
